import React, { useCallback, useContext, useEffect, useState } from 'react'
import * as Type from 'redux/actions/types'
import * as Styled from './Compare.styles'

// component stack
import { Link } from 'components/atoms'
import {
  CompareSlider,
  NavigationDropDown,
  NavigationTabs,
} from 'components/molecules'
import { DesktopCompare } from './partials'

// router && libs && hooks
import qs from 'query-string'
import { TextContext } from 'hocs'
import { useLocation } from 'react-router-dom'
import {
  getDeepCopy,
  getNumberOfBookmarks,
  getViewHeadline,
} from 'settings/app/helpers'
import {
  getLocationParams,
  getPlainComparePath,
  getSearchGetParams,
} from 'settings/app/urlHelpers'
import { useDispatch, useSelector } from 'react-redux'
import {
  entryPointId,
  ADDITIONAL_SEARCHBAR_SELECTS,
} from 'settings/app/constants'
import {
  getCompareItems,
  setActiveElements,
  resetCompareState,
  getAllDetails,
} from 'redux/actions/compareActions'
import { hideAPIErrorInfo } from 'redux/actions/errorActions'
import { removeBookmark } from 'redux/actions/bookmarkActions'
import { useHistory } from 'react-router'
import { useMobileSwitch, usePrevious } from 'hooks'
import Bookmark from 'components/organisms/SearchHeader/partials/Bookmark/Bookmark'

/**
 * functional container for all compare views
 * @returns {*}
 * @constructor
 */
const Compare = () => {
  const location = useLocation()
  const textContext = useContext(TextContext)
  const state = useSelector((state) => state)
  const dispatch = useDispatch()
  const history = useHistory()
  const isMobile = useMobileSwitch()

  // local component states && destructing props
  const [source] = useState(getLocationParams(location.search).source) // local || url
  const [compareWithOutSearchTerm, setCompareWithOutSearchTerm] =
    useState(false)
  const [searchTerm, setSearchTerm] = useState(null)
  const [openAccordionKey, setOpenAccordionKey] = useState('')
  const [disableToggleBookmark, setDisableToggleBookmark] = useState(false)
  const { bookmarks: bookmarksState, compare: CompareState } = state
  const selectedType =
    CompareState.activeElements.type ||
    getLocationParams(location.search).resultType
  const { activeIds } = CompareState.activeElements
  const prevMobile = usePrevious(isMobile)
  const renderResults =
    CompareState.activeElements.type === selectedType &&
    activeIds.length > 0 &&
    !!selectedType &&
    CompareState.types[selectedType]?.results.length > 0

  // loader state (header first xhr, details 2nd xhr)
  const [isLoading, setIsLoading] = useState({ header: false, details: false })

  // xhr flags > initial: flag first xhr (general information's and compare data)
  const { initial: initialXHR } = CompareState.xhr[selectedType]
  const details = CompareState.xhr[selectedType]

  /**
   * get item without treatment reason
   * @type {(function(): void)|*}
   */
  const fetchWithoutTreatmentParams = useCallback(() => {
    const { source, ids } = getLocationParams(location.search)
    let bookmarkIds

    if (source === Type.SOURCE_TYPE_LOCAL) {
      bookmarkIds = bookmarksState.types[selectedType].items
    } else {
      bookmarkIds = ids
    }

    // setIsLoading({ header: true, details: false })
    dispatch(
      getCompareItems({
        type: selectedType,
        bookmarkIds,
        isMobile,
        reloadWithOutTreatment: true,
      })
    )
    //   .then(() => {
    //   setIsLoading({ header: false, details: false })
    // })
    //   .catch(() => {
    //     setIsLoading({ header: false, details: false })
    //   })
  }, [bookmarksState.types, dispatch, isMobile, location.search, selectedType])

  /**
   * set number of booked item && searchTerm (use bookmarks state)
   * required for tab navigation
   */
  useEffect(() => {
    dispatch(hideAPIErrorInfo())
  }, [dispatch])

  /**
   * set number of booked item && searchTerm (use bookmarks state)
   * required for tab navigation
   */
  useEffect(() => {
    switch (source) {
      case Type.SOURCE_TYPE_LOCAL:
        setSearchTerm(bookmarksState.types[selectedType].searchParams.query)
        setDisableToggleBookmark(bookmarksState.types[selectedType].count <= 2)
        break
      case Type.SOURCE_TYPE_URL:
        setSearchTerm(
          // bookmarksState.types[selectedType].searchParams.searchTerm,
          bookmarksState.types[selectedType].searchParams.query
        )
        setDisableToggleBookmark(bookmarksState.selectedDetails.count <= 2)
        break
      default:
        return false
    }
  }, [bookmarksState, selectedType, source, location.search])

  /**
   * first xhr 1 - general && compare data
   * fetch names from bookmarked items for select (ids)
   */
  useEffect(() => {
    // only load data once
    if (!initialXHR) {
      const { source, ids } = getLocationParams(location.search)
      let bookmarkIds

      // get bookmark ids
      switch (source) {
        // local storage
        case Type.SOURCE_TYPE_LOCAL:
          bookmarkIds = bookmarksState.types[selectedType].items
          break
        // get params (currently only one typ is included at the get params / active tab from bookmark list [prevView])
        case Type.SOURCE_TYPE_URL:
          bookmarkIds = ids
          break

        default:
          return false
      }

      if (!compareWithOutSearchTerm) {
        setIsLoading({ header: true, details: false })
        dispatch(
          getCompareItems({
            type: selectedType,
            bookmarkIds,
            isMobile,
          })
        )
          .then(() => {
            setIsLoading({ header: false, details: false })
          })
          .catch(() => {
            setIsLoading({ header: false, details: false })
          })
      }
    } else {
      if (compareWithOutSearchTerm && selectedType === entryPointId.HOSPITALS) {
        fetchWithoutTreatmentParams()
      }
    }
  }, [
    location,
    selectedType,
    dispatch,
    bookmarksState,
    compareWithOutSearchTerm,
    initialXHR,
    isMobile,
    fetchWithoutTreatmentParams,
  ])

  /**
   * trigger 2nd xhr
   * force loading details data
   */
  useEffect(() => {
    if (activeIds.length > 0) {
      const notLoadedId = activeIds.filter((x) => !details.details.includes(x))

      if (notLoadedId.length > 0) {
        setIsLoading({ header: false, details: true })
        dispatch(getAllDetails(activeIds, compareWithOutSearchTerm))
          .then(() => {
            setIsLoading({ header: false, details: false })
          })
          .catch(() => {
            setIsLoading({ header: false, details: false })
          })
      }
    }
  }, [activeIds, dispatch, compareWithOutSearchTerm, details.details])

  /**
   * hook for mobile state has changed
   * mobile state all data will initial load
   * desktop state max three items will load
   * if the state changes, then the active ids should modified
   */
  useEffect(() => {
    if (prevMobile !== undefined && prevMobile !== isMobile) {
      if (!isMobile && activeIds.length > 3) {
        const activeElements = {
          type: selectedType,
          activeIds: activeIds.slice(0, 3),
        }
        dispatch(setActiveElements(activeElements))
      }
      if (isMobile) {
        let activeIds = [...bookmarksState.types[selectedType].items]
        if (selectedType === entryPointId.DOCTORS) {
          activeIds = activeIds.map((id) => parseInt(id, 10))
        }
        const activeElements = {
          type: selectedType,
          activeIds,
        }
        dispatch(setActiveElements(activeElements))
      }
    }
  }, [isMobile, dispatch, activeIds, selectedType, prevMobile, bookmarksState])

  const getBackPath = () => {
    const locationParams = getLocationParams(location.search)
    if (locationParams.source === Type.SOURCE_TYPE_URL) {
      return qs.stringifyUrl({
        url: '/merkliste/',
        query: {
          type: locationParams.type,
          ids: locationParams.ids.join(','),
          ...getSearchGetParams({ selectedType, locationParams }),
        },
      })
    }

    // otherwise route back to 'Merkliste'
    return '/merkliste'
  }

  // get tab information's array
  const getTabItems = () => {
    const tabItemArray = []

    if (getNumberOfBookmarks({ bookmarkState: bookmarksState.types }) > 0) {
      Object.keys(bookmarksState.types).forEach((key) => {
        if (bookmarksState.types[key].count < 2) return true

        tabItemArray.push({
          id: key,
          label: `${ textContext[`bookmark.title.${ key }`] } (${
            bookmarksState.types[key].count
          })`,
        })
      })
    }

    return tabItemArray
  }

  /**
   * remove item from bookmark
   **/
  const removeItemFromBookmarks = ({ id, type }) => {
    dispatch(removeBookmark({ id, type, actionLocation: 'compareView' }))
  }

  /**
   *  get option object for select
   * remove the other active elements from options / (currentID will remove with excludeCurrentValue)
   * otherwise the select component could not render the current value
   **/
  const selectOptions = (currentID) => {
    const activeElementsWithCurrent = CompareState.types[
      selectedType
    ].results.filter(
      (result) => !activeIds.includes(result.id) || result.id === currentID
    )
    return activeElementsWithCurrent.map((result) => ({
      value: result.id,
      option: result.locationName,
    }))
  }

  /**
   * returns a complete object bases on id and selectedType
   * @param resultID
   * @returns {*}
   */
  const getObjectById = (resultID) => {
    const result = CompareState.types[selectedType].results.find(
      (result) => result.id === resultID
    )

    // add additional props for hospitals
    if (selectedType === entryPointId.HOSPITALS) {
      result.generalTooltips = CompareState.types[selectedType].generalTooltips
      result.isApo = CompareState.types[selectedType].isAop
    }
    return result
  }

  /**
   * 1. dispatch state change (activeElements) which represent the current compared items
   * 2. set the local state for selected type
   **/
  const handleTabChange = (type) => {
    history.push(getPlainComparePath(type, 'local'))

    // reset compare with out Search Term to initial value
    if (compareWithOutSearchTerm) {
      setCompareWithOutSearchTerm(false)
    }

    // if next selected type has no previously loaded result set activeIds to empty array because ac will set this values
    const activeIdsArray =
      CompareState.types[type].results.length > 0
        ? CompareState.types[type].results.filter((result, index) =>
            index <= 2 ? result.id : false
          )
        : []

    const activeElements = {
      type,
      activeIds: activeIdsArray.map((result) => result.id),
    }
    dispatch(setActiveElements(activeElements))
  }

  /**
   * replace and id and force state change
   **/
  const handleSelectChange = ({ addId, replaceId }) => {
    const activeIdsArray = getDeepCopy(activeIds)
    const index = activeIdsArray.indexOf(replaceId)

    if (index !== -1) {
      activeIdsArray[index] = addId
    }

    const activeElements = {
      type: selectedType,
      activeIds: activeIdsArray,
    }

    dispatch(setActiveElements(activeElements))
  }

  /**
   * search term for rendering in message box (only hospital)
   **/
  const getSearchTerm = () => {
    if (searchTerm === null) return false
    return searchTerm !== '' && !compareWithOutSearchTerm
      ? searchTerm
      : textContext['compare.searchHint.withOutTreatmentReason']
  }

  /**
   * search term for rendering in message box (only hospital)
   **/
  const getCareSearchTerm = () => {
    switch (selectedType) {
      case entryPointId.CAREHOMES:
        const carehomes_focus = ADDITIONAL_SEARCHBAR_SELECTS[
          entryPointId.CAREHOMES
        ][0].options.find(
          (option) =>
            option.value ===
            bookmarksState.types[entryPointId.CAREHOMES].searchParams
              .carehomes_focus
        )?.label
        const carehomes_care_typ = ADDITIONAL_SEARCHBAR_SELECTS[
          entryPointId.CAREHOMES
        ][1].options.find(
          (option) =>
            option.value ===
            bookmarksState.types[entryPointId.CAREHOMES].searchParams
              .carehomes_care_typ
        )?.label

        return (
          <>
            {'den pflegefachlichen Schwerpunkt '}
            <span className='searchTerm'>»{carehomes_focus}«</span>
            {' und die Art des Pflegeplatzes '}
            <span className='searchTerm'>»{carehomes_care_typ}«</span>
          </>
        )
      case entryPointId.CARESERVICES:
        const careservices_term = ADDITIONAL_SEARCHBAR_SELECTS[
          entryPointId.CARESERVICES
        ][0].options.find(
          (option) =>
            option.value ===
            bookmarksState.types[entryPointId.CARESERVICES].searchParams
              .PE_ANG_KRANKENPFLEGE
        )?.label

        return (
          <>
            {'die Art des Pflege- und Betreuungsdienstes '}
            <span className='searchTerm'>»{careservices_term}«</span>
          </>
        )
      case entryPointId.DAYANDNIGHTCARE:
        const dayandnightcare_care_typ = ADDITIONAL_SEARCHBAR_SELECTS[
          entryPointId.DAYANDNIGHTCARE
        ][0].options.find(
          (option) =>
            option.value ===
            bookmarksState.types[entryPointId.DAYANDNIGHTCARE].searchParams
              .PR_TYP
        )?.label
        const dayandnightcare_care_level = ADDITIONAL_SEARCHBAR_SELECTS[
          entryPointId.DAYANDNIGHTCARE
        ][1].options.find(
          (option) =>
            option.value ===
            bookmarksState.types[entryPointId.DAYANDNIGHTCARE].searchParams
              .PR_PFGRAD
        )?.label

        return (
          <>
            {'den pflegefachlichen Schwerpunkt '}
            <span className='searchTerm'>»{dayandnightcare_care_typ}«</span>
            {' bei '}
            <span className='searchTerm'>»{dayandnightcare_care_level}«</span>
          </>
        )
    }
  }

  /**
   * request hospitals data without search term (@message box)
   **/
  const performCompareWithoutSearchTerm = (e) => {
    e.preventDefault()
    setCompareWithOutSearchTerm(true)
  }

  return (
    <Styled.CompareView>
      {/* header content */}
      <h1>{getViewHeadline({ selectedType, view: 'compare' })}</h1>
      <div className='link-header'>
        <Link
          onClick={ () => dispatch(resetCompareState()) }
          isNativeLink={ false }
          to={{
            pathname: '/merkliste',
            ...(source === Type.SOURCE_TYPE_URL && { search: location.search }),
            state: { lastEntryPoint: selectedType },
          }}
          className='link back-link'>
          {textContext['navigation.backToBookmarks']}
        </Link>

        <Bookmark
          count={
            source === Type.SOURCE_TYPE_URL
              ? bookmarksState.selectedDetails.results.length
              : bookmarksState.totalCount
          }
          onBookmarkButtonClicked={ () =>
            history.push({
              pathname: '/merkliste',
              ...(source === Type.SOURCE_TYPE_URL && {
                search: location.search,
              }),
              state: { lastEntryPoint: selectedType },
            })
          }
          className='bookmark-button'
          data-cy='searchbar-bookmark'
        />
      </div>

      {/* compare navigation */}
      {isMobile ? (
        <NavigationDropDown
          items={ getTabItems() }
          selectedId={ selectedType }
          onSelect={ (type) => handleTabChange(type) }
          locked={ getTabItems().length < 2 }
        />
      ) : (
        <NavigationTabs
          className={ `${ getTabItems().length === 1 ? 'full-size' : null }` }
          items={ getTabItems() }
          selectedId={ selectedType }
          onSelect={ (type) => handleTabChange(type) }
        />
      )}

      <div data-cy='compare'>
        {renderResults && isMobile ? (
          <CompareSlider
            selectedType={ selectedType }
            getObjectById={ (resultID) => getObjectById(resultID) }
            removeItemFromBookmarks={ removeItemFromBookmarks }
            compareWithOutSearchTerm={ compareWithOutSearchTerm }
            getBackPath={ getBackPath }
            openAccordionKey={ openAccordionKey }
            setOpenAccordionKey={ (accordionKey) =>
              setOpenAccordionKey(
                accordionKey === openAccordionKey ? '' : accordionKey
              )
            }
            disableToggleBookmark={ disableToggleBookmark }
          />
        ) : (
          <DesktopCompare
            getSearchTerm={ getSearchTerm }
            getCareSearchTerm={ getCareSearchTerm }
            removeItemFromBookmarks={ removeItemFromBookmarks }
            getObjectById={ (resultID) => getObjectById(resultID) }
            getBackPath={ getBackPath }
            handleSelectChange={ handleSelectChange }
            performCompareWithoutSearchTerm={ performCompareWithoutSearchTerm }
            selectOptions={ selectOptions }
            selectedType={ selectedType }
            searchTerm={ searchTerm }
            compareWithOutSearchTerm={ compareWithOutSearchTerm }
            renderResults={ renderResults }
            openAccordionKey={ openAccordionKey }
            setOpenAccordionKey={ (accordionKey) =>
              setOpenAccordionKey(
                accordionKey === openAccordionKey ? '' : accordionKey
              )
            }
            isLoading={ isLoading }
            disableToggleBookmark={ disableToggleBookmark }
          />
        )}
      </div>
    </Styled.CompareView>
  )
}

export default Compare
